import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ReportTypeCode, type ReportRow } from '@/core/models/reports.models';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyBlobError, notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { reportsService } from '@/core/services/reports';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { ClosePopup } from '@/kit/ClosePopup';
import { Icon } from '@/kit/Icon';
import { openDialog } from '@/kit/dialog';
import { SendTrialBalance } from './SendTrialBalance';

/**
 * Reports > CIC Reports (BUG-062): a community interest company's CIC34 reports, one per annual accounts report. Each can
 * be downloaded as its own PDF (the accounts no longer carry it), or edited on Report Settings > CIC34 Report.
 */
export function CicReports() {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  const [rows, setRows] = useState<ReportRow[] | null>(null);

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    reportsService.getReports(client.id, ReportTypeCode.Annual).then(setRows, (error) => notifyError(error));
    // ngOnInit: once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCic = async (row: ReportRow) => {
    try {
      await reportsService.downloadCicReport(row.id);
    } catch (error) {
      notifyBlobError(error, 'The CIC34 report could not be produced.');
    }
  };

  const load = () => {
    if (!client) return;
    reportsService.getReports(client.id, ReportTypeCode.Annual).then(setRows, (error) => notifyError(error));
  };

  const emailCic = async (row: ReportRow) => {
    try {
      const draft = await reportsService.getReportEmailDraft(row.id);
      openDialog(SendTrialBalance, {
        data: {
          title: 'Send CIC34 Report',
          draft: { ...draft, attachmentName: 'CIC34 Report' },
          send: (payload) => reportsService.sendCicReport(row.id, payload),
          downloadAttachment: () => reportsService.downloadCicReport(row.id),
        },
      });
    } catch (error) {
      notifyError(error);
    }
  };

  const remove = async (row: ReportRow) => {
    const confirmed = await openDialog(ClosePopup, { data: { itemName: 'Report', count: 1 } }).afterClosed();
    if (!confirmed) return;
    try {
      await reportsService.deleteReports([row.id]);
    } catch (error) {
      notifyError(error);
      return;
    }
    notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
    load();
  };

  const columns: DataTableColumn<ReportRow>[] = [
    { id: 'refNo', header: 'Ref. No.', cell: (row) => row.refNo },
    { id: 'from', header: 'From Date', cell: (row) => row.fromDate },
    { id: 'to', header: 'To Date', cell: (row) => row.toDate },
    { id: 'description', header: 'Description', cell: (row) => row.description },
    { id: 'updated', header: 'Last Updated', cell: (row) => row.updated },
    {
      id: 'action',
      header: 'Action',
      cell: (row) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Download CIC34 report (PDF)" onClick={() => void downloadCic(row)}>
            <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
          </Button>
          <Button title="Email the CIC34 report" onClick={() => void emailCic(row)}>
            <Icon name="mail_outline" outlined />
          </Button>
          <Button title="Edit the CIC34 report" onClick={() => navigate('/home/taxreturns/cic34-report')}>
            <Icon name="edit" outlined />
          </Button>
          <Button title="Delete" onClick={() => void remove(row)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <SideMenuPage menu="reports">
      <div className="sidenav-details">
        <div className="sidenav-header">
          <div className="d-flex align-items-center justify-space-between">
            <h2>CIC Reports</h2>
            <Button
              className="action-button primary-button"
              onClick={() => navigate('/home/taxreturns/addnewreport?type=0&cic=1')}
            >
              <Icon name="add" />
              <span>Add CIC Report</span>
            </Button>
          </div>
        </div>
        <div className="sidenav-body pt-1">
          <p className="font-size-12 text-gray mb-1">
            Each CIC34 report belongs to a set of annual accounts: add the accounts, complete the CIC34 report on Report
            Settings, then download it here. The accounts PDF no longer carries the CIC34 report.
          </p>
          <DataTable
            columns={columns}
            rows={rows ?? []}
            rowKey={(row) => row.id}
            noDataMessage={rows === null ? 'Loading…' : 'No CIC reports found. Add the annual accounts first.'}
          />
        </div>
      </div>
    </SideMenuPage>
  );
}
